/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface TeamFormProps {
  defaultValues?: any;
  view?: boolean;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function TeamForm({ defaultValues = {}, view = false }: TeamFormProps) {
  const validationToast = useFormValidationToast();
  const { itemId, institutionId } = useParams();

  const baseUrl = '/v1/admin/teams';
  const { requestApi: reqApiTeams, error: errorTeams } = useFetch(baseUrl, {
    actionFeedback: false,
  });
  const {
    requestApi: reqApiTeachers,
    data: dataTeachers,
    loading: loadingTeachers,
  } = useFetch(
    institutionId
      ? `/v1/admin/users?group_id=4&institution_id=${institutionId}`
      : '/v1/admin/users?group_id=4'
  );
  const { data: optionsTeachers, pagination: paginationTeachers } = getFetchData(dataTeachers);
  const {
    requestApi: reqApiModules,
    data: dataModules,
    loading: loadingModules,
  } = useFetch('/v1/admin/classes');
  const { data: optionsModules, pagination: paginationModules } = getFetchData(dataModules);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome da turma',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'teacher_ids',
      value: defaultValues?.teachers?.length
        ? defaultValues.teachers.map((teacher: any) => getFormattedLabel(teacher))
        : defaultValues?.teacher
        ? [getFormattedLabel(defaultValues.teacher)]
        : [],
      placeholder: 'Selecione os professores',
      label: 'Professores',
      yup: Yup.array()
        .of(Yup.mixed())
        .min(1, 'Selecione pelo menos um professor')
        .required('Este campo é obrigatório!'),
    },
    {
      name: 'school_class_id',
      value: defaultValues?.school_class ? getFormattedLabel(defaultValues?.school_class) : null,
      placeholder: 'Selecione o módulo',
      label: 'Módulo',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object: any) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field: any) => field.name === 'name');
  const module = formInputs.find((field: any) => field.name === 'school_class_id');
  const teachersField = formInputs.find((field: any) => field.name === 'teacher_ids');
  const status = formInputs.find((field: any) => field.name === 'status');
  const normalizedInstitutionId =
    defaultValues?.institution?.id ?? (institutionId ? Number(institutionId) : undefined);

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      institution_id: normalizedInstitutionId,
      school_class_id: values.school_class_id.value,
      teacher_ids: values.teacher_ids?.map((teacher: any) => teacher.value) ?? [],
    };
    await reqApiTeams(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage={`institutions/list/${institutionId}/teams`} mb={5} />
        <PageTitle>{view ? 'Visualizar' : defaultValues.name ? 'Editar' : 'Criar'} Turma</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!errorTeams
              ? `Turma ${defaultValues.name ? 'editada' : 'criada'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} a turma.`}
          </Text>
        }
        msgStatus={errorTeams ? 'error' : 'success'}
        closeOnOverlayClick={!!errorTeams}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate(`/institutions/list/${institutionId}/teams`)}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>

        <Flex gap={5}>
          <Box flex={5}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled={view}
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              isDisabled={view}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        <Flex gap={5}>
          <Box flex={1}>
            <SelectInputForm
              name={module?.name}
              label={module?.label}
              options={[...(optionsModules?.map((item: any) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingModules}
              pagination={paginationModules}
              getOptions={(filters: any) => reqApiModules('get', { params: { ...filters } })}
              isRequired
              isDisabled={view}
              placeholder={module?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SelectInputForm
              name={teachersField?.name}
              label={teachersField?.label}
              options={[...(optionsTeachers?.map((item: any) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingTeachers}
              pagination={paginationTeachers}
              getOptions={(filters: any) => reqApiTeachers('get', { params: { ...filters } })}
              isRequired
              isDisabled={view}
              placeholder={teachersField?.placeholder}
              isMulti
              control={control}
              errors={errors}
            />
          </Box>
        </Flex>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting}
            type="submit"
            isDisabled={view}
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
